import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CaseComposer } from "./CaseComposer";
import { CaseFollowUps } from "./CaseFollowUps";
import { CasePriorities } from "./CasePriorities";
import { CaseConditions } from "./CaseConditions";
import { CaseChoices } from "./CaseChoices";
import { CaseCompareTable } from "./CaseCompareTable";
import { CaseBrowse } from "./CaseBrowse";
import { CaseHelp } from "./CaseHelp";
import { CaseCurrentPlan } from "./CaseCurrentPlan";

const PLACEHOLDER = {
  consumer: "Example: I live in ZIP 33134, see Dr. Maria Garcia, take metformin 500 mg, have diabetes, no Medicaid. I care most about a Part B premium reduction and groceries.",
  agent: "Example: Senior in ZIP 32168, sees Dr. Smith (cardiology), diabetic with a heart condition, no Medicaid. Wants Part B reduction and grocery benefits — top three side by side.",
};

export const DecisionCase = ({ base, variant = "consumer", contact, onRequestAdvisor, onCase, seed, onOpenLegacy }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resumed, setResumed] = useState(false);
  const set = useCallback((d) => { if (d?.case_id) { setC(d); onCase?.(d); } }, [onCase]);
  const topRef = useRef(null);
  const call = async (fn, toTop = false) => {
    setBusy(true);
    try { set((await fn()).data); if (toTop) topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong. Please try again."); }
    setBusy(false);
  };
  useEffect(() => { if (base) api.get(base).then((r) => { set(r.data); setResumed(!!r.data?.result); }).catch(() => {}); }, [base, set]);
  const seeded = useRef(null);
  useEffect(() => {
    if (!base || !seed || seeded.current === seed) return;
    seeded.current = seed;
    call(() => api.post(`${base}/message`, { text: seed }), true);
  }, [base, seed]); // eslint-disable-line react-hooks/exhaustive-deps
  const reading = c?.result?.choices?.some((x) => x.sob?.status === "extracting");
  useEffect(() => {
    if (!reading) return undefined;
    const t = setInterval(() => { api.get(base).then((r) => set(r.data)).catch(() => {}); }, 6000);
    return () => clearInterval(t);
  }, [reading, base, set]);

  const r = c?.result;
  const last = (c?.transcript || []).filter((t) => t.role === "assistant").slice(-1)[0];
  const toggle = (id) => {
    const cur = (c.selected_candidates || []).map((s) => s.display_id);
    if (!cur.includes(id) && cur.length >= 3) { toast.error("The comparison holds 3 plans. Remove one, then add this plan."); return; }
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    call(() => api.post(`${base}/select`, { display_ids: next }));
  };
  const viewCompare = () => document.getElementById("case-comparison")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div ref={topRef} className="min-w-0 space-y-5" data-testid={`decision-case-${variant}`}>
      {resumed && variant === "consumer" && c?.facts?.zip && c?.result && <p className="text-sm text-[#1F5132]" data-testid="case-resumed-note">Your saved plan search{c.facts.county ? ` for ${c.facts.county} County` : ""} is below. Add or correct anything and the results update.</p>}
      {busy && <p className="flex items-center gap-2 rounded-xl bg-[#FFFBEA] p-3 text-sm text-[#5A4A1A]" data-testid="case-working"><Loader2 className="h-4 w-4 animate-spin" />Working on it — checking plans, doctors and medicines…</p>}
      {last && <p className="rounded-xl bg-white p-4 text-base text-[#0B192C] shadow-sm" data-testid="case-assistant-reply">{last.text}</p>}
      <CaseFollowUps followUps={c?.follow_ups} busy={busy} onSend={(t) => call(() => api.post(`${base}/message`, { text: t }), true)}
        onConfirm={(p) => call(() => api.post(`${base}/confirm`, p))} />
      {c?.facts?.county && <CaseConditions conditions={c.facts.conditions} status={c.facts.conditions_status} busy={busy} agent={variant === "agent"}
        onChange={(p) => call(() => api.post(`${base}/conditions`, p))} />}
      <CaseComposer busy={busy} placeholder={c ? "Add or correct anything (doctors, medicines, priorities)…" : PLACEHOLDER[variant]}
        onSend={(t) => call(() => api.post(`${base}/message`, { text: t }), true)} />
      {c && <CaseCurrentPlan base={base} current={c.facts.current_plan} hint={c.facts.current_plan_hint} hasCounty={!!c.facts.county} onCase={set} agent={variant === "agent"} />}
      {c && <CasePriorities priorities={c.facts.priorities} labels={c.priority_labels} busy={busy}
        onChange={(p) => call(() => api.post(`${base}/priorities`, { priorities: p }))} />}
      {r && (
        <>
          <p className="text-sm text-[#5A6B7B]" data-testid="case-scope-note">{r.scope_note}{r.priorities_defaulted ? " No priorities given yet, so this ranks by low out-of-pocket max and premium." : ""}</p>
          {r.choices.length ? <CaseChoices choices={r.choices} selected={c.selected_candidates} busy={busy} onToggleSelect={toggle} agent={variant === "agent"}
            currentId={c.facts.current_plan?.card_display_id} onViewCompare={viewCompare}
            onSetCurrent={(id) => call(() => api.post(`${base}/current-plan`, { from_candidate: id }))} />
            : <p className="text-base text-[#0B192C]" data-testid="case-no-choices">No evaluated plans matched. A licensed advisor can review other options with you.</p>}
          {variant === "agent" && <p className="text-sm text-[#33475B]" data-testid="case-compare-count">Comparing {(c.selected_candidates || []).length} of 3 plans. Remove any plan to swap in another.</p>}
          {variant === "agent" && <CaseBrowse alternatives={r.alternatives} selectedCount={(c.selected_candidates || []).length} busy={busy}
            onAdd={(id) => call(() => api.post(`${base}/add-plan`, { display_id: id }))} />}
          <CaseCompareTable comparison={r.comparison} only={(c.selected_candidates || []).map((s) => s.display_id)} />
          {onOpenLegacy && <button type="button" onClick={onOpenLegacy} className="text-sm text-[#1E3E62] underline" data-testid="case-open-legacy-compare">Browse every plan in your county in a table instead</button>}
          {variant === "consumer" && <CaseHelp handoff={r.handoff} contact={contact} onRequestAdvisor={onRequestAdvisor} />}
          {variant === "agent" && r.handoff?.checks?.length > 0 && (
            <div className="rounded-xl border border-[#D9D6CC] bg-white p-4 text-sm" data-testid="case-agent-checklist">
              <p className="font-semibold text-[#0B192C]">Checklist before recommending</p>
              <ul className="mt-1 list-disc pl-5 text-[#33475B]">{r.handoff.checks.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};
